import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { usePasswordRules } from "@jti/rest-api";
import { useFormContext } from "../../components/Form";
import { Info } from "../../components/Info/Info";
import { Loading } from "../../components/Loading/Loading";
import { useTranslations } from "../../providers/useTranslations";
import { validationPatterns } from "../../utils/validationPatterns";
import styles from "./PasswordRequirementsBlock.module.scss";
const { CONTAINS_LOWER_CASE, CONTAINS_NUMBER, CONTAINS_UPPER_CASE, SPECIAL_CHARACTER } = validationPatterns;
const MAX_PASSWORD_LENGTH = 50;
export const PasswordRequirementsBlock = ({ passwordValue, emailValue }) => {
    const { __ } = useTranslations();
    const VALIDATION_KEY = "password";
    const { setError, clearErrors } = useFormContext();
    const [validationObj, setValidationObj] = useState({
        passwordMinLength: false,
        passwordMaxLength: false,
        containNumber: false,
        containSpecial: false,
        containLettersLowerCase: false,
        containLettersUpperCase: false,
    });
    const [validationInfoMessages, setValidationInfoMessages] = useState({
        passwordMinLength: __("auth.registration.password_info_message_length"),
        passwordMaxLength: __("auth.registration.password_info_message_max_length"),
        containSpecial: __("auth.registration.password_info_message_character"),
        containLettersLowerCase: __("auth.registration.password_info_message_letter_lower_case"),
        containLettersUpperCase: __("auth.registration.password_info_message_letter_upper_case"),
        containNumber: __("auth.registration.password_info_message_number"),
    });
    const { data: passwordRules, isLoading } = usePasswordRules();
    const isRulesInvalid = (validationObject) => {
        return Object.values(validationObject).some((value) => {
            return !value;
        });
    };
    const passMailForOktaVerification = useCallback((email) => {
        const [addressPart, domainPart] = email?.toLowerCase().split("@") || [];
        if (addressPart && domainPart) {
            const addressSplitted = addressPart?.split(".");
            const domain = domainPart?.split(".");
            if (addressSplitted.length && domain.length) {
                return ![...addressSplitted, domain[0]].some((part) => passwordValue?.toLowerCase().includes(part));
            }
        }
        return true;
    }, [passwordValue]);
    useEffect(() => {
        if (passwordRules && !isLoading) {
            const validationObject = {
                passwordMinLength: passwordValue.length >= passwordRules.minLength,
                passwordMaxLength: passwordValue.length > 0 && passwordValue.length <= MAX_PASSWORD_LENGTH,
                containNumber: (passwordValue.match(CONTAINS_NUMBER) || []).length >= passwordRules.minNumber,
                containSpecial: (passwordValue.match(SPECIAL_CHARACTER) || []).length >= passwordRules.minSymbol,
                containLettersLowerCase: (passwordValue.match(CONTAINS_LOWER_CASE) || []).length >= passwordRules.minLowerCase,
                containLettersUpperCase: (passwordValue.match(CONTAINS_UPPER_CASE) || []).length >= passwordRules.minUpperCase,
                excludeUsername: !passwordRules.excludeUsername || (passMailForOktaVerification(emailValue) && passwordValue.length),
            };
            if (isRulesInvalid(validationObject)) {
                setError(VALIDATION_KEY, {
                    type: "manual",
                });
            }
            else {
                clearErrors(VALIDATION_KEY);
            }
            setValidationInfoMessages({
                passwordMinLength: __("auth.registration.password_info_message_length", [passwordRules.minLength]),
                passwordMaxLength: __("auth.registration.password_info_message_max_length"),
                containSpecial: __("auth.registration.password_info_message_character", [passwordRules.minSymbol]),
                containLettersLowerCase: __("auth.registration.password_info_message_letter_lower_case", [
                    passwordRules.minLowerCase,
                ]),
                containLettersUpperCase: __("auth.registration.password_info_message_letter_upper_case", [
                    passwordRules.minUpperCase,
                ]),
                containNumber: __("auth.registration.password_info_message_number", [passwordRules.minNumber]),
                excludeUsername: __("auth.registration.password_exclude_username_rule"),
            });
            setValidationObj(validationObject);
        }
    }, [passwordRules, passwordValue, isLoading, passMailForOktaVerification, emailValue, __, setError, clearErrors]);
    const filterNotEmptyRules = (key) => {
        if (passwordRules) {
            switch (key) {
                case "passwordMinLength":
                    return passwordRules.minLength > 0;
                case "passwordMaxLength":
                    return true;
                case "containNumber":
                    return passwordRules.minNumber > 0;
                case "containSpecial":
                    return passwordRules.minSymbol > 0;
                case "containLettersLowerCase":
                    return passwordRules.minLowerCase > 0;
                case "containLettersUpperCase":
                    return passwordRules.minUpperCase > 0;
                case "excludeUsername":
                    return passwordRules.excludeUsername;
                default:
                    return false;
            }
        }
        return true;
    };
    const filterValidationRules = () => {
        return Object.entries(validationObj)
            .filter(([key]) => filterNotEmptyRules(key))
            .map(([key, value]) => (_jsx(Info, { className: styles.iconClassName, variant: value ? "checkmark" : "info", message: validationInfoMessages[key], dataTestId: value ? `${key}Checkmark` : `${key}Info`, textClassName: styles.requirementDescription }, key)));
    };
    const renderValidationRules = () => {
        if (isLoading && !passwordRules) {
            return _jsx(Loading, { dataTestId: "passwordRequirementsLoader" });
        }
        return (_jsx("div", { className: styles.infoBlock, "data-testid": "passwordValidationRules", children: filterValidationRules() }));
    };
    return renderValidationRules();
};
//# sourceMappingURL=PasswordRequirementsBlock.js.map